// =============================================================================
// src/lib/eligibility.js - Medicaid eligibility status for visits.
//
//   useVisitEligibility(appointmentIds) -> { byId, refresh }
//       byId[appointment_id] = { medicaid, status, checked_at, message,
//                                reviewed, auto, check_id }
//   eligibilityView(row) -> { label, variant, title, problem } or null
//
// visit_eligibility (migration 20261003120000_auto_eligibility) returns the
// latest NCTracks check covering each visit: one tied to the visit, or one
// about any day of the visit's month (Medicaid eligibility runs by calendar
// month). Automatic checks run 2 business days ahead and the evening before
// for practices that turned them on (Settings > Automations).
// =============================================================================
import { useCallback, useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "./supabaseClient";

export * from "./eligibilityCore.js";
import { plainMessage } from "./eligibilityCore.js";

// --- Eligibility page (Eligibility Pulse v2, migration 20261009100000) ------
//   eligibility_pulse_summary(days)  header numbers, outage, set-up state
//   eligibility_upcoming(days)       visits in the next N business days
//   eligibility_review_queue(filter, limit, offset)  flagged checks (no X12)
//   eligibility_changes()            coverage changes to act on
//   eligibility_review(id, outcome, note) / eligibility_ack_change(...)
async function rpc(name, args) {
  const { data, error } = await supabase.rpc(name, args);
  if (error) throw error;
  return data;
}
const Q = { staleTime: 30 * 1000, refetchInterval: 2 * 60 * 1000 };
export const useEligibilitySummary = (days, enabled = true) =>
  useQuery({ queryKey: ["elig-summary", days], queryFn: () => rpc("eligibility_pulse_summary", { p_days: days }), enabled, ...Q });
export const useEligibilityUpcoming = (days, enabled = true) =>
  useQuery({ queryKey: ["elig-upcoming", days], queryFn: () => rpc("eligibility_upcoming", { p_days: days }).then((r) => r || []), enabled, ...Q });
export const useEligibilityQueue = (filter, limit, enabled = true) =>
  useQuery({ queryKey: ["elig-queue", filter, limit], queryFn: () => rpc("eligibility_review_queue", { p_filter: filter, p_limit: limit, p_offset: 0 }).then((r) => r || { total: 0, rows: [] }), enabled, ...Q });
export const useEligibilityChanges = (enabled = true) =>
  useQuery({ queryKey: ["elig-changes"], queryFn: () => rpc("eligibility_changes", {}).then((r) => r || []), enabled, ...Q });

export const reviewEligibilityCheck = (checkId, outcome, note) =>
  rpc("eligibility_review", { p_check_id: checkId, p_outcome: outcome, p_note: note || null });
export const ackEligibilityChange = (row, outcome = "handled", note = null) =>
  rpc("eligibility_ack_change", { p_patient_id: row.patient_id, p_kind: row.kind, p_check_id: row.check_id, p_outcome: outcome, p_note: note });

// Run a real-time NCTracks check. forVisit: { appointmentId, serviceDate }
// makes the result count for that visit's month; retryOf re-runs (and closes)
// an earlier check. Resolves { ok, status, checkId, message }.
export async function runEligibilityCheck({ patientId, appointmentId = null, serviceDate = null, retryOf = null }) {
  const body = { patient_id: patientId };
  if (appointmentId) body.appointment_id = appointmentId;
  if (serviceDate) body.service_date = serviceDate;
  if (retryOf) body.retry_of = retryOf;
  const { data, error } = await supabase.functions.invoke("eligibility-check", { body });
  let out = data;
  // A failed check still records a row and answers 502 with its check_id;
  // functions.invoke puts non-2xx bodies on error.context.
  if (!out && error && error.context && typeof error.context.json === "function") {
    try { out = await error.context.json(); } catch { out = null; }
  }
  if (out && out.check_id) return { ok: true, status: out.status, checkId: out.check_id, message: out.error || null };
  if (out && out.error) throw new Error(out.error);
  throw new Error("Could not reach the eligibility service. Please try again shortly.");
}

export function useVisitEligibility(appointmentIds, enabled = true) {
  const key = (appointmentIds || []).filter(Boolean).slice().sort().join(",");
  const [byId, setById] = useState({});
  const refresh = useCallback(async () => {
    if (!enabled || !key) { setById({}); return; }
    const { data, error } = await supabase.rpc("visit_eligibility", { p_appointment_ids: key.split(",") });
    if (error) { console.warn("[eligibility] status load failed:", error.message); return; }
    const m = {};
    (data || []).forEach((r) => { m[r.appointment_id] = r; });
    setById(m);
  }, [key, enabled]);
  useEffect(() => { refresh(); }, [refresh]);
  return { byId, refresh };
}

function when(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" }) + " " +
    d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
}

// Badge content for a visit. Nothing for patients without Medicaid on file
// or visits not checked yet (no noise on the schedule).
export function eligibilityView(row) {
  if (!row || !row.medicaid || !row.status) return null;
  const how = (row.auto ? "Automatic check " : "Checked ") + when(row.checked_at);
  if (row.status === "Verified") {
    return { label: "Coverage OK", variant: "green", problem: false, title: "Medicaid coverage verified. " + how };
  }
  if (row.status === "Pending") {
    return { label: "Coverage pending", variant: "neutral", problem: false, title: "Eligibility check sent, waiting for NCTracks. " + how };
  }
  const reviewed = row.reviewed ? " Reviewed by staff." : "";
  if (row.status === "Error") {
    return { label: "Check failed", variant: "amber", problem: !row.reviewed, title: "The eligibility check could not complete" + (row.message ? ": " + plainMessage(row.message) : "") + ". " + how + "." + reviewed };
  }
  return {
    label: row.status === "Inactive" ? "Coverage inactive" : "Coverage problem",
    variant: row.reviewed ? "amber" : "red",
    problem: !row.reviewed,
    title: (plainMessage(row.message) || "Medicaid coverage not verified") + ". " + how + "." + reviewed,
  };
}
